import React from "react";
import PageLayout from "../components/PageLayout";
import ProjectCard from "../components/ProjectCard";
import Spacer from "../components/Spacer";

const Projects = () => {
  return (
    <PageLayout>
      <div className="text-[1.18rem] my-6 2xl:text-[1.35rem] font-lusitana">
        {/* Project Cards */}
        <div className="flex flex-col space-y-6">
          <ProjectCard
            title="Saylo"
            subtitle="Web App | 2024 - Now"
            githubLink="https://github.com/zhucs/saylo"
          >
            <Spacer height="0.2rem"/>
            <p>
            Saylo is a <span className="font-bold">language learning app</span> I developed to help people learn language the way it’s really used: as a tool to do things. Through context-based exercises, Saylo teaches the action-driven ways we use language to navigate the world: asking for help, sharing opinions, resolving misunderstandings, setting plans, and more.
            <Spacer height="0.4rem"/>
            Check out the demo below to see it in action!
            </p>
            <iframe
              className="mt-2 border border-black"
              width="100%"
              height="315"
              src="https://youtube.com/embed/yd1nXpRnrfw"
              title="YouTube Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <p className="mt-4 mb-2">A closer look at Saylo's context-based exercises: </p>
            <img
              src="/project-pics/saylo-exercises.png"
              alt="Saylo Screenshot"
              className="my-2 border border-black"
            />
            Saylo is the first app to ever teach language in this way. Take <span className="italic">“Oh, really?”</span> as a rhetorical question: it doesn’t just exist—it performs an action, expressing sarcasm to show disapproval, much like a wave signals goodbye or pushing a door opens a room. Fluency is about knowing how to <span className="italic">get things done</span> in your target language, and Saylo teaches you how!
            
            <Spacer height="1rem"/>
            <h2 className="text-[1.6rem] font-bold">The Tech Stack</h2>
            <Spacer height="0.4rem"/>
            Saylo's frontend is built with <span className="font-bold">React</span> and styled with{" "}
            <span className="font-bold">Tailwind CSS</span>. The backend uses{" "}
            <span className="font-bold">Express</span> and <span className="font-bold">Supabase</span>{" "}
            for data storage and user management. <span className="font-bold">OpenAI’s GPT</span>{" "}
            powers the dynamic creation of interactive exercise materials. This stack supports
            real-time interaction and scalability for future updates.

            <Spacer height="1rem"/>
            <h2 className="text-[1.6rem] font-bold">What's To Come</h2>
            <Spacer height="0.4rem"/>
            <ul className="list-disc pl-5 mb-1">
              <li>User testing on interface and exercise content</li>
              <li>Expanding content and adding new modules</li>
              <li>Deploying features like progress tracking and other analytics</li>
            </ul>            
          </ProjectCard>

          <ProjectCard
            title="Nit"
            subtitle="Web App | 2024"
            githubLink="https://github.com/devon-campbell/nit"
          >
            <Spacer height="0.2rem"/>
            <p>
            Nit is a <span className="font-bold">music learning app </span> that makes learning sight-reading and piano possible from your laptop. Featuring an interactive keyboard and metronome interface, lessons, and quizzes, Nit offers a personalized learning experience, from basic note lessons to advanced free-play—all without requiring a physical piano.
            <Spacer height="0.4rem"/>
            </p>
            <iframe
              className="mt-2 border border-black"
              width="100%"
              height="315"
              src="https://www.youtube.com/embed/kOISxvSq9BE"
              title="YouTube Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <Spacer height="1.4rem"/>
            <h2 className="text-[1.6rem] font-bold">Challenges and Innovations</h2>
            <Spacer height="0.4rem"/>
            We faced two key challenges during development: the design challenge of delivering clear, intuitive feedback on user
            performance and the technical challenge of parsing, diffing, and modifying
            <span className="font-bold"> MusicXML </span> to achieve it. To tackle the first, I created a
            system for dynamically generating <span className="font-bold">SVG-based</span> practice
            materials, ensuring fresh, high-quality content for every session. For the second, I built a
            <span className="font-bold"> Python-based MusicXML parser</span> which uses a diffing algorithm
            to compare input against target sheet
            music and color-highlighting mistakes in real time. The result: delivering precise feedback via a
            user-friendly interface.    
            <Spacer height="0.4rem"/>        
            <div class="flex flex-col items-center">
              <img
                src="project-pics/nit-code.png"
                alt="Nit Code & Diff UI"
                className="my-2 border border-black"
              />
              <div className="italic">
                Snippet from parser code; colored MusicXML notes for feedback
              </div>
            </div>
            
            <Spacer height="1.4rem"/>
            <h2 className="text-[1.6rem] font-bold">The Tech Stack</h2>
            <Spacer height="0.4rem"/>
            We built Nit with a <span className="font-bold">Flask</span> backend and a{" "}
            <span className="font-bold">PostgreSQL</span> database. The UI was developed entirely with{" "}
            <span className="font-bold">React</span>, featuring an interactive piano interface and dynamically
            generated SVG music notes. <span className="font-bold">Python</span> powered core functionality,
            including the MusicXML parser, sheet music diffing, and random note generation, ensuring a smooth
            and scalable experience.
          </ProjectCard>


          <ProjectCard
            title="Polyphony"
            subtitle="Web App | 2024"
            githubLink="https://github.com/sanjaynatesan/DevFest2024-Polyphony"
          >
            <Spacer height="0.2rem"/>
            <p>
            Polyphony is a <span className="font-bold">music recommendation app</span> that helps users discover songs based on their mood. Built with my team during <span className="font-bold">DevFest 2024</span>, Columbia's annual hackathon, it enhances Spotify’s recommendation engine with custom machine learning to predict users’ emotional states. Users train the app through interactive sessions, creating personalized playlists that align with their feelings.
            <Spacer height="0.4rem"/>
            </p>
            <iframe
              className="mt-2 border border-black"
              width="100%"
              height="315"
              src="https://youtube.com/embed/bfDoXr4QUFw"
              title="YouTube Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>         
            <Spacer height="1.2rem"/>
            <h2 className="text-[1.6rem] font-bold">Challenges and Innovations</h2>
            <Spacer height="0.4rem"/>
            Navigating the Spotify API’s strict rate limits and development key constraints was a major challenge. Despite these hurdles and just a few days to build, we delivered a functional prototype with an ML model that accurately predicted users' moods. Our collaborative workflow allowed us to overcome obstacles and create a cohesive, engaging app under tight time constraints.
   
            <Spacer height="1rem"/>
            <h2 className="text-[1.6rem] font-bold">The Tech Stack</h2>
            <Spacer height="0.4rem"/>
            We built Polyphony using various tools, including a <span className="font-bold">React.js</span> frontend and a <span className="font-bold">Flask</span> backend. Our database runs on <span className="font-bold">PostgreSQL</span>, hosted with <span className="font-bold">Google Cloud Platform</span>, and we relied on libraries like <span className="font-bold">scikit-learn</span> and <span className="font-bold">nltk</span> for machine learning and natural language processing. Services like <span className="font-bold">Google Gemini</span> and <span className="font-bold">Vertex AI</span> helped power predictive features.

          </ProjectCard>

          <ProjectCard
            title="Wordrive"
            subtitle="Extension | 2023"
            githubLink="https://github.com/zhucs/wordrive"
            link="https://chromewebstore.google.com/detail/wordrive/jcfkmmdnkgfdlihjejjlfgilcikfifok"
          >
            <Spacer height="0.2rem"/>
            Wordrive is a <span className="font-bold">Google Chrome Extension</span> that lets readers and language learners save and organize words as they read. Users can highlight words, save them with their source URLs, and access features like sorting and exporting, all through a simple, non-intrusive interface. Designed for students and avid readers, Wordrive focuses on streamlined vocabulary tracking over complex tools. Available for download on the Chrome Extensions store!
            <Spacer height="0.4rem"/>
            <div class="flex flex-col items-center">
              <img
                src="project-pics/wordrive-desktop-ss.avif"
                alt="Wordrive on Browser"
                className="my-2 border border-black"
              />
              <div className="italic">
                Using Wordrive on a scientific article
              </div>
            </div>
            
            <Spacer height="1.4rem"/>
            <h2 className="text-[1.6rem] font-bold">Challenges and Innovations</h2>
            <Spacer height="0.4rem"/>
              <span className="font-bold">1) Design:</span> I designed Wordrive with web-using readers and language learners in mind—people who encounter new vocabulary all the time and want a simple way to track it. The challenge was creating a lightweight, intuitive extension that felt like a seamless part of their reading experience. 
              <Spacer height="0.4rem"/>
              <div class="flex flex-col items-center">
                <img
                  src="project-pics/wordrive-design-sketch.avif"
                  alt="Wordrive Sketch"
                  className="my-2 border border-black"
                />
                <div className="italic">
                  My initial UI sketches
                </div>
              </div>
              <Spacer height="0.8rem"/>
              <span className="font-bold">2) Development:</span> I implemented core features like word saving, history tracking, and exporting, ensuring users could quickly organize their vocabulary without disrupting their workflow. The UI was designed to be unobtrusive, allowing actions like adding, deleting, and sorting words with minimal effort. I also integrated dynamic reference links and export functionality.

            <Spacer height="1.4rem"/>
            <h2 className="text-[1.6rem] font-bold">The Tech Stack</h2>
            <Spacer height="0.4rem"/>
            Wordrive was built with <span className="font-bold">JavaScript, HTML, <span className="font-normal">and</span> CSS</span>, leveraging Chrome’s{" "}
            <span className="font-bold">Extensions API</span> for features including saving words and
            local storage. The frontend was styled with <span className="font-bold">CSS</span>, and the
            back-end logic handled features like exporting to <span className="font-bold">CSV</span> files
            and dynamically linking saved words to reference materials.
          </ProjectCard>

          <ProjectCard
            title="My Website"
            subtitle="Website | 2025"
          >
          I had a blast building this website. It's a tribute to Windows 95, in all of its charming simplicity and tactile realism. My favorite part of the Windows 95 design language is the beveled windows and buttons because they provide a sense of depth. GUIs styled like this make it seem like you’re working with a real bulletin board or flipping through a book.
          
          <div class="flex flex-col items-center">
                <img
                  src="project-pics/desktopss.png"
                  alt="Windows 95 Desktop"
                  className="my-2 border border-black"
                />
                <div className="italic">
                  Actual Windows 95 Desktop
                </div>
              </div>
              <Spacer height="0.6rem"/>

          The header font you see around the site is Apple Garamond, the typeface Apple used in its web and print materials in the late 80s. It's paired with the font of the words you're reading right now, Lusitana.
            <div class="flex flex-col items-center">
                <img
                  src="project-pics/apple-brand-guidelines.png"
                  alt="Wordrive Sketch"
                  className="my-2 border border-black"
                />
                <div className="italic">
                  Apple's branding guidelines circa 1987
                </div>
              </div>
              <Spacer height="0.6rem"/>
              Thank you for stopping by my site! If you have any questions or comments, feel free to reach out—I’d love to hear from you.
          </ProjectCard>
        </div>
      </div>
    </PageLayout>
  );
};

export default Projects;
